import { createContext, type FC, type ReactNode, useContext, useEffect, useState } from "react";
import type { MomentFormMoment } from "../../../../modules/moment/types/moment-form-moment";
import type { UseSupabaseLoaderState } from "../../../../server/supabase/hooks/types";
import type { UUID } from "../../../../utils/uuid";
import { useMomentDeliveredsTotals } from "../../analytics/hooks/use-moment-delivereds-totals";
import { useMomentAnalyticsTotals } from "../../analytics/hooks/use-moment-stats-totals";
import { useMomentPollsResults } from "../hooks/use-moment-polls-results";
import { calculatePollsResults } from "./calculate-poll-results";

export type PollRecipient = {
  destination: string | null;
  display: string | null;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  person_id: UUID;
};

export type PollRecipientResponse = PollRecipient & {
  timestamp: string;
};

export type ResultDisplay = {
  text: string;
  count: number;
  percentage: number;
  responses: PollRecipientResponse[];
};
export type RenderPollResults = {
  poll_id: UUID;
  results: ResultDisplay[];
  question: string;
  hasResponsed: boolean;
  noResponses: PollRecipient[];
  totalResponses: number;
  sentTimes: number;
};
type MomentPollsContextType = UseSupabaseLoaderState<RenderPollResults[]>;

const initialState: MomentPollsContextType = {
  data: null,
  loading: true,
  error: null,
};

const MomentPollsContext = createContext<MomentPollsContextType>(initialState);

// This contains the poll results for  all the polls in the moment along with
// the deliveries, and calculates who has responded to each poll.

export const MomentPollsContextProvider: FC<{
  children: ReactNode;
  moment: MomentFormMoment;
}> = ({ children, moment }) => {
  const [value, setValue] = useState<MomentPollsContextType>(initialState);

  const momentPollResults = useMomentPollsResults(moment);
  const momentAnalyticsTotals = useMomentAnalyticsTotals(moment);
  const momentDeliveredTotals = useMomentDeliveredsTotals(moment.id);

  useEffect(() => {
    if (
      momentPollResults.data?.length &&
      momentAnalyticsTotals.data &&
      momentDeliveredTotals.data
    ) {
      const data = calculatePollsResults(
        momentPollResults.data,
        momentAnalyticsTotals.data,
        momentDeliveredTotals.data,
      );
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setValue({
        error: null,
        loading: false,
        data,
      });
    } else if (momentPollResults.error || momentAnalyticsTotals.error) {
      setValue({
        error: momentPollResults.error || momentAnalyticsTotals.error || new Error("Unknown error"),
        loading: false,
        data: null,
      });
    }
  }, [
    momentPollResults.data,
    momentPollResults.error,
    momentAnalyticsTotals.data,
    momentAnalyticsTotals.error,
    momentDeliveredTotals.data,
  ]);

  return <MomentPollsContext.Provider value={value}>{children}</MomentPollsContext.Provider>;
};

export const useMomentPollsContext = (): MomentPollsContextType => {
  const context = useContext(MomentPollsContext);
  if (!context) {
    throw new Error("useMomentAnalyticsContext be used within a MomentAnalyticsProvider");
  }
  return context;
};
